import React, { useEffect, useCallback } from 'react';
import CategoriesCarousel from '@stitch-fix/mode-react/CategoriesCarousel';
import { useInView } from 'react-intersection-observer';
import { useProductDetailsPageContext } from '../ProductDetailsPage/ProductDetailsPageContext/ProductDetailsPageContext';
import {
  trackCarouselScroll,
  trackCategoryClick,
  trackCategoryEndCardClick,
  trackCategoryEndCardView,
  trackSeeMoreCategoriesClick,
  trackCarouselSeenInViewport,
} from './ShopCategoriesCarousel.tracking';
import CategoriesCarouselEventTracker from '../Gated/src/tracking/CategoriesCarouselEventTracker';

/**
 * Ungated department carousel
 */
const ShopCategoriesCarousel = () => {
  const [state] = useProductDetailsPageContext();

  const defaultTrackingState = {
    clientBusinessLine: state.clientBusinessLine,
    productBusinessLine: state.productBusinessLine,
    skipEventTracking: state.skipEventTracking,
    source: state.source,
    subSource: state.subSource,
  };

  const [shopCategoriesInViewRef, shopCategoriesInView] = useInView({
    triggerOnce: true,
  });
  const setShopCategoriesRefs = useCallback(
    node => {
      shopCategoriesInViewRef(node);
    },
    [shopCategoriesInViewRef],
  );

  useEffect(() => {
    if (shopCategoriesInView) {
      if (state.gated) {
        const categoryCards = state.shopCategories?.categorySet?.cards || [];
        const shopCategories = categoryCards.map(card => {
          return {
            id: card.category?.id,
            name: card.title,
            image: card.image,
          };
        });

        CategoriesCarouselEventTracker.trackCarouselSeenInViewport({
          categories: shopCategories,
          source: state.source,
        });
      } else {
        trackCarouselSeenInViewport({
          categories: state.shopCategories?.categorySet?.cards || [],
          clientBusinessLine: state.clientBusinessLine,
          productBusinessLine: state.productBusinessLine,
          skipEventTracking: state.skipEventTracking,
          subSource: state.subSource,
        });
      }
    }
  }, [
    shopCategoriesInView,
    state.clientBusinessLine,
    state.productBusinessLine,
    state.shopCategories,
    state.skipEventTracking,
    state.subSource,
    state.gated,
    state.source,
  ]);

  const carouselVariant = (): 'icon' | 'editorial' => {
    switch (state.shopCategories?.categorySet?.cardLayout?.toLowerCase()) {
      case 'icon':
        return 'icon';
      case 'editorial':
        return 'editorial';
      default:
        return 'icon';
    }
  };

  return (
    <div
      ref={setShopCategoriesRefs as unknown as React.RefObject<HTMLDivElement>}
    >
      <CategoriesCarousel
        ariaLabel={state.shopCategories?.title || ''}
        gutterOverflow={{ sm: 1, lg: 2, xl: 0 }}
        categorySetId={state.shopCategories?.categorySet.id || ''}
        categoryCards={state.shopCategoriesCards}
        isLoading={state.shopCategoriesLoading}
        moduleHeader={{
          header: state.shopCategories?.title,
          cta: {
            url: state.shopCategoriesEndCard?.cta?.href || '',
            text: state.shopCategories?.cta?.text,
            onClick: () => {
              if (state.gated) {
                CategoriesCarouselEventTracker.trackSeeMoreCategoriesClick({
                  // even though a category id does NOT make sense for an
                  // end card eventing requires the feild so we send an empty string
                  categoryId: '',
                  source: state.source,
                });
              } else {
                trackSeeMoreCategoriesClick(defaultTrackingState);
              }
            },
          },
          mb: 'default',
        }}
        onCategoryClick={categoryId => {
          if (state.gated) {
            CategoriesCarouselEventTracker.trackCategoryClick({
              categoryId,
              source: state.source,
            });
          } else {
            trackCategoryClick({
              ...defaultTrackingState,
              categoryId,
            });
          }
        }}
        onCategoryView={categoryId => {
          if (state.gated) {
            CategoriesCarouselEventTracker.trackCarouselScroll({
              categoryId,
              source: state.source,
            });
          } else {
            trackCarouselScroll({
              ...defaultTrackingState,
              categoryId,
            });
          }
        }}
        onEndCardClick={() => {
          if (state.gated) {
            CategoriesCarouselEventTracker.trackCategoryEndCardClick({
              // even though a category id does NOT make sense for an
              // end card eventing requires the feild so we send an empty string
              categoryId: '',
              source: state.source,
            });
          } else {
            trackCategoryEndCardClick(defaultTrackingState);
          }
        }}
        onEndCardView={() => {
          if (state.gated) {
            CategoriesCarouselEventTracker.trackCategoryEndCardView({
              // even though a category id does NOT make sense for an
              // end card eventing requires the feild so we send an empty string
              categoryId: '',
              source: state.source,
            });
          } else {
            trackCategoryEndCardView(defaultTrackingState);
          }
        }}
        variant={carouselVariant()}
      />
    </div>
  );
};

export default ShopCategoriesCarousel;
